import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Collapse,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { $authHost } from '../../../http';
import {
  decodeLogItem,
  formatValueForDisplay,
  getActionColors,
  getActionText
} from '../../../utils/logDecoder';

const AuditHistoryDialog = ({
  open,
  onClose,
  tableName,
  tableTitle,
  recordId,
  recordTitle = '',
  darkMode = false,
  translates = {},
  formatting = {},
  ignore = [],
  fieldPathHandler = null
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Загрузка истории изменений
  useEffect(() => {
    if (open && tableName && recordId) {
      loadHistory();
    }
  }, [open, tableName, recordId, translates]);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await $authHost.get(`/apig/audit/${tableName}/${recordId}?limit=100&offset=0`);
      const { success, data: auditData } = response.data;

      // Обрабатываем каждый элемент лога, используя только formatting
      let decodedHistory = success ? auditData.map(logItem =>
        decodeLogItem(logItem, {}, {}, translates, formatting, ignore)
      ) : [];

      // Обрабатываем пути через fieldPathHandler если он передан
      if (fieldPathHandler) {
        decodedHistory = decodedHistory.map(logItem => {
          if (!logItem.changed_fields || logItem.changed_fields.length === 0) {
            return logItem;
          }

          const processedFields = {};
          const processedFieldLabels = {};
          const remainingFields = [];

          logItem.changed_fields.forEach(fieldPath => {
            // Проверяем, является ли путь сложным (data.categories[...])
            if (fieldPath.split('.').length > 0) {
              const pathInfo = fieldPathHandler(
                fieldPath,
                logItem.old_values,
                logItem.new_values,
                formatting
              );

              if (pathInfo) {
                processedFields[fieldPath] = pathInfo;
                processedFieldLabels[fieldPath] = pathInfo.fieldLabel;
              } else {
                remainingFields.push(fieldPath);
              }
            } else {
              remainingFields.push(fieldPath);
            }
          });

          return {
            ...logItem,
            processedFields,
            processedFieldLabels,
            remainingFields
          };
        });
      }

      setHistory(decodedHistory);
      setFilteredHistory(decodedHistory);

    } catch (err) {
      console.error('Error loading audit history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация истории по поисковому запросу
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredHistory(history);
      setExpandedRows(new Set()); // Скрываем все при очистке поиска
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = history.filter(item => {
      // Поиск по пользователю
      const userNameMatch = item.user_name?.toLowerCase().includes(query) || 
                           item.username?.toLowerCase().includes(query);
      
      // Поиск по действию
      const actionMatch = getActionText(item.action).toLowerCase().includes(query);
      
      // Поиск по дате
      const dateMatch = formatDate(item.created_at).toLowerCase().includes(query);
      
      // Поиск по измененным полям
      const fieldsMatch = item.changed_fields?.some(field => {
        const fieldTranslation = item.field_translations?.[field] || field;
        return fieldTranslation.toLowerCase().includes(query);
      });
      
      // Поиск по значениям полей
      const valuesMatch = item.changed_fields?.some(field => {
        const oldValue = item.old_values?.[field];
        const newValue = item.new_values?.[field];
        const oldValueStr = formatValueForSearch(oldValue, field, formatting, item.old_values).toLowerCase();
        const newValueStr = formatValueForSearch(newValue, field, formatting, item.new_values).toLowerCase();
        return oldValueStr.includes(query) || newValueStr.includes(query);
      });

      return userNameMatch || actionMatch || dateMatch || fieldsMatch || valuesMatch;
    });

    setFilteredHistory(filtered);

    // Автоматически раскрываем строки с найденными совпадениями
    if (filtered.length > 0) {
      const newExpandedRows = new Set();
      filtered.forEach(item => {
        // Проверяем, есть ли совпадения в деталях (полях или значениях)
        const hasDetailsMatch = item.changed_fields?.some(field => {
          const fieldTranslation = item.field_translations?.[field] || field;
          const fieldMatch = fieldTranslation.toLowerCase().includes(query);
          
          const oldValue = item.old_values?.[field];
          const newValue = item.new_values?.[field];
          const oldValueStr = formatValueForSearch(oldValue, field, formatting, item.old_values).toLowerCase();
          const newValueStr = formatValueForSearch(newValue, field, formatting, item.new_values).toLowerCase();
          const valueMatch = oldValueStr.includes(query) || newValueStr.includes(query);
          
          return fieldMatch || valueMatch;
        });
        
        // Раскрываем строку, если есть совпадения в деталях
        if (hasDetailsMatch && item.changed_fields && item.changed_fields.length > 0) {
          newExpandedRows.add(item.id);
        }
      });
      
      setExpandedRows(newExpandedRows);
    } else {
      setExpandedRows(new Set());
    }
  }, [searchQuery, history, formatting]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Функция для проверки, является ли значение пустым
  const isValueEmpty = (value) => {
    if (value === null || value === undefined) {
      return true;
    }
    if (value === '') {
      return true;
    }
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    if (typeof value === 'object' && Object.keys(value).length === 0) {
      return true;
    }
    // Проверка на строковые представления пустых значений
    if (typeof value === 'string' && (value === 'null' || value === 'undefined')) {
      return true;
    }
    return false;
  };

  // Функция для конвертации значения в строку для поиска
  const formatValueForSearch = (value, field, formatting, allValues) => {
    const formatted = formatValueForDisplay(value, field, formatting, allValues);
    if (Array.isArray(formatted)) {
      return formatted.map(item => String(item)).join(' ');
    }
    return String(formatted);
  };

  // Функция для подсветки найденного текста
  const highlightText = (text, query) => {
    if (!query || text === null || text === undefined) return text;
    
    // Конвертируем значение в строку, если это не строка
    const textStr = typeof text === 'string' ? text : String(text);
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = textStr.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} style={{ 
          backgroundColor: darkMode ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.4)',
          color: darkMode ? '#fff' : '#000',
          fontWeight: 'bold',
          padding: '1px 2px',
          borderRadius: '2px'
        }}>
          {part}
        </span>
      ) : part
    );
  };

  const handleRowToggle = (rowId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'INSERT':
        return <AddIcon fontSize="small" />;
      case 'UPDATE':
        return <EditIcon fontSize="small" />;
      case 'DELETE':
        return <DeleteIcon fontSize="small" />;
      default:
        return <ViewIcon fontSize="small" />;
    }
  };



  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'HH:mm dd.MM.yyyy ', { locale: ru });
    } catch (error) {
      return dateString;
    }
  };

  const renderChangedFields = (item) => {
    if (!item.changed_fields || item.changed_fields.length === 0) {
      return <Typography variant="body2" sx={{ color: darkMode ? '#e3f2fd' : 'inherit' }}>Нет изменений</Typography>;
    }

    // Объединяем обработанные поля и оставшиеся поля
    const allFields = [];
    
    // Добавляем обработанные поля с их метками
    if (item.processedFieldLabels) {
      Object.entries(item.processedFieldLabels).forEach(([fieldPath, label]) => {
        allFields.push({ path: fieldPath, label, isProcessed: true });
      });
    }
    
    // Добавляем оставшиеся поля (стандартная обработка)
    const remainingFields = item.remainingFields || item.changed_fields.filter(f => !item.processedFieldLabels?.[f]);
    remainingFields.forEach(field => {
      if (!item.processedFieldLabels?.[field]) {
        const fieldTranslation = item.field_translations?.[field] || field;
        allFields.push({ path: field, label: fieldTranslation, isProcessed: false });
      }
    });

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        {allFields.map((fieldInfo, index) => {
          const isDecoded = item.decoded_fields?.[fieldInfo.path]?.decoded || fieldInfo.isProcessed;

          return (
            <Chip
              key={index}
              label={searchQuery ? highlightText(fieldInfo.label, searchQuery) : fieldInfo.label}
              size="small"
              onClick={() => { }}
              sx={{
                bgcolor: isDecoded
                  ? (darkMode ? 'rgba(76, 175, 80, 0.25)' : 'rgba(76, 175, 80, 0.1)')
                  : (darkMode ? 'rgba(255, 193, 7, 0.25)' : 'rgba(255, 193, 7, 0.1)'),
                color: isDecoded
                  ? (darkMode ? '#81c784' : '#4caf50')
                  : (darkMode ? '#ffd54f' : '#f57f17'),
                fontSize: '0.7rem',
                height: '20px',
                border: isDecoded
                  ? (darkMode ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(76, 175, 80, 0.3)')
                  : (darkMode ? '1px solid rgba(255, 193, 7, 0.3)' : 'none'),
              }}
            />
          );
        })}
      </Box>
    );
  };

  const renderValueComparison = (oldValues, newValues, changedFields, fieldTranslations = {}, decodedFields = {}, processedFields = {}) => {
    if (!changedFields || changedFields.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 0 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: darkMode ? '#e3f2fd' : 'inherit' }}>
          Детали изменений:
        </Typography>
        {changedFields.map((field, index) => {
          // Проверяем, обработан ли этот путь через fieldPathHandler
          const processedField = processedFields?.[field];
          
          if (processedField) {
            // Используем обработанные значения
            const oldValue = processedField.oldValue;
            const newValue = processedField.newValue;
            const fieldTranslation = processedField.fieldLabel;
            const isDecoded = true;

          return (
            <Box key={index} sx={{
              mb: 1,
              p: 1,
              bgcolor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderRadius: 1,
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? '#e3f2fd' : 'inherit' }}>
                  {searchQuery ? highlightText(fieldTranslation, searchQuery) : fieldTranslation}:
                </Typography>
                {processedField.changes && (
                  <Chip
                    label={processedField.changes}
                    size="small"
                    sx={{
                      bgcolor: darkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)',
                      color: darkMode ? '#64b5f6' : '#1976d2',
                      fontSize: '0.65rem',
                      height: '18px',
                      border: darkMode ? '1px solid rgba(33, 150, 243, 0.3)' : '1px solid rgba(33, 150, 243, 0.3)',
                    }}
                  />
                )}
                {!isDecoded && (
                  <Chip
                    label="Ошибка расшифровки"
                    size="small"
                    onClick={() => { }}
                    sx={{
                      bgcolor: darkMode ? 'rgba(175, 76, 76, 0.2)' : 'rgba(175, 76, 76, 0.1)',
                      color: darkMode ? '#C78484' : '#AF4A4A',
                      fontSize: '0.6rem',
                      height: '16px',
                      border: darkMode ? '1px solid rgba(175, 76, 76, 0.3)' : '1px solid rgba(175, 76, 76, 0.3)',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ 
                pl: 1,
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <Box sx={{ 
                  flex: 1,
                  p: 1.5,
                  bgcolor: darkMode ? 'rgba(255, 138, 128, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                  borderRadius: 1,
                  border: darkMode ? '1px solid rgba(255, 138, 128, 0.3)' : '1px solid rgba(244, 67, 54, 0.2)'
                }}>
                  <Typography variant="body2" sx={{
                    color: darkMode ? '#ff8a80' : '#f44336',
                    mb: 1,
                    fontWeight: 600,
                    '& strong': { color: darkMode ? '#ffcdd2' : '#d32f2f' }
                  }}>
                    <strong>До:</strong>
                  </Typography>
                  {(() => {
                    // Для обработанных полей значения уже отформатированы в handleFieldPath
                    const formattedOldValue = oldValue;
                    const isEmpty = isValueEmpty(formattedOldValue);
                    const isArray = Array.isArray(formattedOldValue);
                    
                    if (isEmpty) {
                      return (
                        <Typography variant="body2" sx={{
                          color: darkMode ? '#ffcdd2' : '#d32f2f',
                          wordBreak: 'break-word',
                          fontStyle: 'italic'
                        }}>
                          Пусто
                        </Typography>
                      );
                    }
                    
                    if (isArray) {
                      return (
                        <Box component="ul" sx={{ 
                          m: 0, 
                          pl: 2,
                          '& li': {
                            color: darkMode ? '#ffcdd2' : '#d32f2f',
                            wordBreak: 'break-word',
                            mb: 0.5
                          }
                        }}>
                          {formattedOldValue.map((item, idx) => (
                            <li key={idx}>
                              <Typography variant="body2" component="span" sx={{
                                color: darkMode ? '#ffcdd2' : '#d32f2f',
                              }}>
                                {searchQuery ? highlightText(String(item), searchQuery) : String(item)}
                              </Typography>
                            </li>
                          ))}
                        </Box>
                      );
                    }
                    
                    return (
                      <Typography variant="body2" sx={{
                        color: darkMode ? '#ffcdd2' : '#d32f2f',
                        wordBreak: 'break-word'
                      }}>
                        {searchQuery ? highlightText(String(formattedOldValue), searchQuery) : String(formattedOldValue)}
                      </Typography>
                    );
                  })()}
                </Box>
                <Box sx={{ 
                  flex: 1,
                  p: 1.5,
                  bgcolor: darkMode ? 'rgba(165, 214, 167, 0.1)' : 'rgba(76, 175, 80, 0.05)',
                  borderRadius: 1,
                  border: darkMode ? '1px solid rgba(165, 214, 167, 0.3)' : '1px solid rgba(76, 175, 80, 0.2)'
                }}>
                  <Typography variant="body2" sx={{
                    color: darkMode ? '#a5d6a7' : '#4caf50',
                    mb: 1,
                    fontWeight: 600,
                    '& strong': { color: darkMode ? '#c8e6c9' : '#2e7d32' }
                  }}>
                    <strong>После:</strong>
                  </Typography>
                  {(() => {
                    // Для обработанных полей значения уже отформатированы в handleFieldPath
                    const formattedNewValue = newValue;
                    const isEmpty = isValueEmpty(formattedNewValue);
                    const isArray = Array.isArray(formattedNewValue);
                    
                    if (isEmpty) {
                      return (
                        <Typography variant="body2" sx={{
                          color: darkMode ? '#c8e6c9' : '#2e7d32',
                          wordBreak: 'break-word',
                          fontStyle: 'italic'
                        }}>
                          Пусто
                        </Typography>
                      );
                    }
                    
                    if (isArray) {
                      return (
                        <Box component="ul" sx={{ 
                          m: 0, 
                          pl: 2,
                          '& li': {
                            color: darkMode ? '#c8e6c9' : '#2e7d32',
                            wordBreak: 'break-word',
                            mb: 0.5
                          }
                        }}>
                          {formattedNewValue.map((item, idx) => (
                            <li key={idx}>
                              <Typography variant="body2" component="span" sx={{
                                color: darkMode ? '#c8e6c9' : '#2e7d32',
                              }}>
                                {searchQuery ? highlightText(String(item), searchQuery) : String(item)}
                              </Typography>
                            </li>
                          ))}
                        </Box>
                      );
                    }
                    
                    return (
                      <Typography variant="body2" sx={{
                        color: darkMode ? '#c8e6c9' : '#2e7d32',
                        wordBreak: 'break-word'
                      }}>
                        {searchQuery ? highlightText(String(formattedNewValue), searchQuery) : String(formattedNewValue)}
                      </Typography>
                    );
                  })()}
                </Box>
              </Box>
            </Box>
          );
          } else {
            // Стандартная обработка для обычных полей
            const oldValue = oldValues?.[field];
            const newValue = newValues?.[field];
            const fieldTranslation = fieldTranslations[field] || field;
            const decodedField = decodedFields[field];
            const isDecoded = decodedField?.decoded;

            return (
              <Box key={index} sx={{
                mb: 1,
                p: 1,
                bgcolor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                borderRadius: 1,
                border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? '#e3f2fd' : 'inherit' }}>
                    {searchQuery ? highlightText(fieldTranslation, searchQuery) : fieldTranslation}:
                  </Typography>
                  {!isDecoded && (
                    <Chip
                      label="Ошибка расшифровки"
                      size="small"
                      onClick={() => { }}
                      sx={{
                        bgcolor: darkMode ? 'rgba(175, 76, 76, 0.2)' : 'rgba(175, 76, 76, 0.1)',
                        color: darkMode ? '#C78484' : '#AF4A4A',
                        fontSize: '0.6rem',
                        height: '16px',
                        border: darkMode ? '1px solid rgba(175, 76, 76, 0.3)' : '1px solid rgba(175, 76, 76, 0.3)',
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ 
                  pl: 1,
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <Box sx={{ 
                    flex: 1,
                    p: 1.5,
                    bgcolor: darkMode ? 'rgba(255, 138, 128, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                    borderRadius: 1,
                    border: darkMode ? '1px solid rgba(255, 138, 128, 0.3)' : '1px solid rgba(244, 67, 54, 0.2)'
                  }}>
                    <Typography variant="body2" sx={{
                      color: darkMode ? '#ff8a80' : '#f44336',
                      mb: 1,
                      fontWeight: 600,
                      '& strong': { color: darkMode ? '#ffcdd2' : '#d32f2f' }
                    }}>
                      <strong>До:</strong>
                    </Typography>
                    {(() => {
                      // Проверяем исходное значение на пустоту перед форматированием
                      const isEmpty = isValueEmpty(oldValue);
                      const formattedOldValue = formatValueForDisplay(oldValue, field, formatting, oldValues);
                      const isArray = Array.isArray(formattedOldValue);
                      
                      if (isEmpty) {
                        return (
                          <Typography variant="body2" sx={{
                            color: darkMode ? '#ffcdd2' : '#d32f2f',
                            wordBreak: 'break-word',
                            fontStyle: 'italic'
                          }}>
                            Пусто
                          </Typography>
                        );
                      }
                      
                      if (isArray) {
                        return (
                          <Box component="ul" sx={{ 
                            m: 0, 
                            pl: 2,
                            '& li': {
                              color: darkMode ? '#ffcdd2' : '#d32f2f',
                              wordBreak: 'break-word',
                              mb: 0.5
                            }
                          }}>
                            {formattedOldValue.map((item, idx) => (
                              <li key={idx}>
                                <Typography variant="body2" component="span" sx={{
                                  color: darkMode ? '#ffcdd2' : '#d32f2f',
                                }}>
                                  {searchQuery ? highlightText(String(item), searchQuery) : String(item)}
                                </Typography>
                              </li>
                            ))}
                          </Box>
                        );
                      }
                      
                      return (
                        <Typography variant="body2" sx={{
                          color: darkMode ? '#ffcdd2' : '#d32f2f',
                          wordBreak: 'break-word'
                        }}>
                          {searchQuery ? highlightText(String(formattedOldValue), searchQuery) : String(formattedOldValue)}
                        </Typography>
                      );
                    })()}
                  </Box>
                  <Box sx={{ 
                    flex: 1,
                    p: 1.5,
                    bgcolor: darkMode ? 'rgba(165, 214, 167, 0.1)' : 'rgba(76, 175, 80, 0.05)',
                    borderRadius: 1,
                    border: darkMode ? '1px solid rgba(165, 214, 167, 0.3)' : '1px solid rgba(76, 175, 80, 0.2)'
                  }}>
                    <Typography variant="body2" sx={{
                      color: darkMode ? '#a5d6a7' : '#4caf50',
                      mb: 1,
                      fontWeight: 600,
                      '& strong': { color: darkMode ? '#c8e6c9' : '#2e7d32' }
                    }}>
                      <strong>После:</strong>
                    </Typography>
                    {(() => {
                      // Проверяем исходное значение на пустоту перед форматированием
                      const isEmpty = isValueEmpty(newValue);
                      const formattedNewValue = formatValueForDisplay(newValue, field, formatting, newValues);
                      const isArray = Array.isArray(formattedNewValue);
                      
                      if (isEmpty) {
                        return (
                          <Typography variant="body2" sx={{
                            color: darkMode ? '#c8e6c9' : '#2e7d32',
                            wordBreak: 'break-word',
                            fontStyle: 'italic'
                          }}>
                            Пусто
                          </Typography>
                        );
                      }
                      
                      if (isArray) {
                        return (
                          <Box component="ul" sx={{ 
                            m: 0, 
                            pl: 2,
                            '& li': {
                              color: darkMode ? '#c8e6c9' : '#2e7d32',
                              wordBreak: 'break-word',
                              mb: 0.5
                            }
                          }}>
                            {formattedNewValue.map((item, idx) => (
                              <li key={idx}>
                                <Typography variant="body2" component="span" sx={{
                                  color: darkMode ? '#c8e6c9' : '#2e7d32',
                                }}>
                                  {searchQuery ? highlightText(String(item), searchQuery) : String(item)}
                                </Typography>
                              </li>
                            ))}
                          </Box>
                        );
                      }
                      
                      return (
                        <Typography variant="body2" sx={{
                          color: darkMode ? '#c8e6c9' : '#2e7d32',
                          wordBreak: 'break-word'
                        }}>
                          {searchQuery ? highlightText(String(formattedNewValue), searchQuery) : String(formattedNewValue)}
                        </Typography>
                      );
                    })()}
                  </Box>
                </Box>
                {!isDecoded && decodedField?.originalValue !== undefined && (
                  <Typography variant="caption" sx={{
                    color: darkMode ? '#90a4ae' : 'text.secondary',
                    fontStyle: 'italic',
                    display: 'block',
                    mt: 0.5,
                    pl: 1
                  }}>
                    Исходное значение: {formatValueForDisplay(decodedField.originalValue, field, formatting, decodedField)}
                  </Typography>
                )}
              </Box>
            );
          }
        })}
      </Box>
    );
  };

  const handleClose = () => {
    setHistory([]);
    setFilteredHistory([]);
    setError(null);
    setExpandedRows(new Set());
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: darkMode ? '#1a1a1a' : '#fff',
          color: darkMode ? '#f5f7fa' : '#333',
          minHeight: '70vh',
          border: darkMode ? '1px solid rgba(255,255,255,0.12)' : 'none',
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
        borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pr: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{
            bgcolor: darkMode ? '#1976d2' : '#2196f3',
            width: 32,
            height: 32,
            color: darkMode ? '#e3f2fd' : '#fff'
          }}>
            <HistoryIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: darkMode ? '#e3f2fd' : 'inherit' }}>
              История изменений
            </Typography>
            <Typography variant="body2" sx={{ color: darkMode ? '#b0bec5' : 'text.secondary', mt: 0.5 }}>
              {/* {recordTitle && `${recordTitle} • `} */}
              {tableTitle} - {recordTitle}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, maxWidth: 400, ml: 2 }}>
          <TextField
            size="small"
            placeholder="Поиск по истории..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                color: darkMode ? '#e3f2fd' : 'inherit',
                '& fieldset': {
                  borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                },
                '&:hover fieldset': {
                  borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: darkMode ? '#1976d2' : '#1976d2',
                },
                '& input::placeholder': {
                  color: darkMode ? '#90a4ae' : 'rgba(0,0,0,0.6)',
                  opacity: 1,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: darkMode ? '#90a4ae' : 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{
                      color: darkMode ? '#90a4ae' : 'text.secondary',
                      '&:hover': {
                        bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'action.hover'
                      }
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: darkMode ? '#b0bec5' : 'text.secondary',
            '&:hover': {
              bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'action.hover'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress sx={{ color: darkMode ? '#1976d2' : undefined }} />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              variant="outlined"
              onClick={loadHistory}
              sx={{
                color: darkMode ? '#ff8a80' : undefined,
                borderColor: darkMode ? '#ff8a80' : undefined,
                '&:hover': {
                  borderColor: darkMode ? '#ffcdd2' : undefined,
                  bgcolor: darkMode ? 'rgba(255, 138, 128, 0.08)' : undefined,
                }
              }}
            >
              Повторить попытку
            </Button>
          </Box>
        )}

        {!loading && !error && history.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <HistoryIcon sx={{ fontSize: 48, color: darkMode ? '#546e7a' : 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color={darkMode ? '#b0bec5' : 'text.secondary'}>
              История изменений пуста
            </Typography>
            <Typography variant="body2" color={darkMode ? '#90a4ae' : 'text.secondary'}>
              Для этого объекта еще не было зафиксировано изменений
            </Typography>
          </Box>
        )}

        {!loading && !error && history.length > 0 && filteredHistory.length === 0 && searchQuery && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 48, color: darkMode ? '#546e7a' : 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color={darkMode ? '#b0bec5' : 'text.secondary'}>
              Ничего не найдено
            </Typography>
            <Typography variant="body2" color={darkMode ? '#90a4ae' : 'text.secondary'}>
              По запросу "{searchQuery}" не найдено записей
            </Typography>
          </Box>
        )}

        {!loading && !error && filteredHistory.length > 0 && (
          <TableContainer>
            <Table stickyHeader size="small" aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{
                    bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
                    minWidth: 50,
                    color: darkMode ? '#e3f2fd' : 'inherit',
                    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : undefined
                  }}>
                    #
                  </TableCell>
                  <TableCell sx={{
                    bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
                    minWidth: 120,
                    color: darkMode ? '#e3f2fd' : 'inherit',
                    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : undefined
                  }}>
                    Действие
                  </TableCell>
                  <TableCell sx={{
                    bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
                    minWidth: 150,
                    color: darkMode ? '#e3f2fd' : 'inherit',
                    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : undefined
                  }}>
                    Пользователь
                  </TableCell>
                  <TableCell sx={{
                    bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
                    minWidth: 180,
                    color: darkMode ? '#e3f2fd' : 'inherit',
                    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : undefined
                  }}>
                    Дата и время
                  </TableCell>
                  <TableCell sx={{
                    bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
                    minWidth: 200,
                    color: darkMode ? '#e3f2fd' : 'inherit',
                    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : undefined
                  }}>
                    Измененные поля
                  </TableCell>
                  <TableCell sx={{
                    bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
                    minWidth: 60,
                    color: darkMode ? '#e3f2fd' : 'inherit',
                    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : undefined
                  }}>
                    Детали
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory.map((item, index) => {
                  const actionColors = getActionColors(item.action, darkMode);
                  const isExpanded = expandedRows.has(item.id);

                  return (
                    <React.Fragment key={item.id}>
                      <TableRow sx={{
                        '&:hover': {
                          bgcolor: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'
                        },
                        '& td': {
                          borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : undefined
                        }
                      }}>
                        <TableCell sx={{
                          fontWeight: 600,
                          color: darkMode ? '#e3f2fd' : 'inherit'
                        }}>
                          {filteredHistory.length - index}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getActionIcon(item.action)}
                            label={getActionText(item.action)}
                            size="small"
                            onClick={() => { }}
                            sx={{
                              bgcolor: actionColors.bg,
                              color: actionColors.color,
                              border: `1px solid ${actionColors.border}40`,
                              fontWeight: 500,
                              '& .MuiChip-icon': {
                                color: actionColors.color
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{
                              width: 24,
                              height: 24,
                              bgcolor: darkMode ? '#7b1fa2' : '#9c27b0',
                              color: darkMode ? '#f3e5f5' : '#fff'
                            }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{
                                fontWeight: 500,
                                color: darkMode ? '#e3f2fd' : 'inherit'
                              }}>
                                {item.user_name || 'Неизвестно'}
                              </Typography>
                              {item.username && (
                                <Typography variant="caption" color={darkMode ? '#90a4ae' : 'text.secondary'}>
                                  @{item.username}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon fontSize="small" color={darkMode ? "primary" : "action"} />
                            <Typography variant="body2" sx={{ color: darkMode ? '#e3f2fd' : 'inherit' }}>
                              {formatDate(item.created_at)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {renderChangedFields(item)}
                        </TableCell>
                        <TableCell>
                          {item.changed_fields && item.changed_fields.length > 0 && (
                            <Tooltip title={isExpanded ? "Скрыть детали" : "Показать детали"}>
                              <IconButton
                                size="small"
                                onClick={() => handleRowToggle(item.id)}
                                sx={{
                                  color: darkMode ? '#b0bec5' : 'text.secondary',
                                  '&:hover': {
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : undefined
                                  }
                                }}
                              >
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                      {item.changed_fields && item.changed_fields.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{
                                p: 2,
                                bgcolor: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                                borderTop: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)'
                              }}>
                                {renderValueComparison(
                                  item.old_values,
                                  item.new_values,
                                  item.changed_fields,
                                  item.field_translations,
                                  item.decoded_fields,
                                  item.processedFields
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{
        borderTop: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
        bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
        px: 3,
        py: 2,
      }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            color: darkMode ? '#b0bec5' : undefined,
            borderColor: darkMode ? '#546e7a' : undefined,
            '&:hover': {
              borderColor: darkMode ? '#90a4ae' : undefined,
              bgcolor: darkMode ? 'rgba(176, 190, 197, 0.08)' : undefined,
            }
          }}
        >
          Закрыть
        </Button>
        {!loading && !error && filteredHistory.length > 0 && (
          <Button
            onClick={loadHistory}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              ml: 1,
              bgcolor: darkMode ? '#1976d2' : undefined,
              color: darkMode ? '#e3f2fd' : undefined,
              '&:hover': {
                bgcolor: darkMode ? '#1565c0' : undefined,
              }
            }}
          >
            Обновить
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AuditHistoryDialog; 
